<template>
  <div>
    <!-- Save Report Builder Format Modal -->
    <b-modal
      id="saveReportFormatFormId"
      ref="saveReportFormatForm"
      ok-title="Save"
      @ok.prevent="saveReportFormat"
      centered
      modal-class="modern-modal"
    >
      <b-card class="modal-card">
        <div class="modal-header-section mb-2">
          <h5 class="mb-1">Save Report Format</h5>
          <small class="text-muted"
            >Give this format a friendly name so you can reuse it later.</small
          >
        </div>
        <b-form>
          <b-form-group label="Format Name" label-class="form-label">
            <b-form-input
              v-model="reportFormatName"
              class="modern-input"
              placeholder="e.g. Sales by Territory – Detailed"
            />
          </b-form-group>
        </b-form>
      </b-card>
    </b-modal>

    <!-- Drag Object Options -->
    <b-modal
      id="columnOptionsId"
      ref="columnOptionsRef"
      :title="selectedCol.name"
      hide-footer
      centered
      no-close-on-esc
      no-close-on-backdrop
      size="lg"
      modal-class="modern-modal"
    >
      <b-form
        @submit.prevent.stop="saveColumnOptions"
        :key="columnOptionsModalKey"
      >
        <b-card class="modal-card">
          <div class="modal-header-section mb-2">
            <h5 class="mb-0">Configure "{{ selectedCol.name }}"</h5>
            <small class="text-muted"
              >Set filters and pivoting options for this column.</small
            >
          </div>

          <b-card
            v-if="
              selectedColSchema.categories &&
              selectedColSchema.categories.length
            "
            class="section-card mb-2"
          >
            <div class="section-header mb-2">
              <feather-icon icon="LayersIcon" size="16" class="mr-1" />
              <strong>Synopsis Categories</strong>
            </div>
            <b-form-checkbox
              v-for="cat in selectedColSchema.categories"
              :key="'category_' + cat.key"
              v-model="columnOptions.categorySelections[cat.key]"
              class="mb-1"
            >
              {{ cat.name }}
            </b-form-checkbox>
          </b-card>

          <template
            v-if="
              selectedColSchema.categories &&
              selectedColSchema.categories.length
            "
          >
            <b-card
              v-for="cat in activeSynopsisCategories"
              :key="'category_filters_' + cat.key"
              class="section-card mb-2"
            >
              <div class="section-header mb-1">
                <feather-icon icon="FilterIcon" size="16" class="mr-1" />
                <strong>{{ cat.name }} Filters</strong>
              </div>
              <b-row>
                <b-col
                  v-for="(item, index) in cat.filters"
                  :key="'category_filter_input_' + cat.key + index"
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b-form-group
                    :label="item.name"
                    label-class="form-label"
                    class="mb-0"
                  >
                    <v-select
                      v-if="item.type === 'select'"
                      label="name"
                      :options="filtersOptions[item.list_route]"
                      v-model="
                        columnOptions.filtersUI[
                          getCategoryFilterUiKey(cat.key, item.key)
                        ]
                      "
                      @input="onFilterInput($event, item, cat.key)"
                    />

                    <v-select
                      v-else-if="item.type === 'multi-select'"
                      label="name"
                      :options="filtersOptions[item.list_route]"
                      v-model="
                        columnOptions.filtersUI[
                          getCategoryFilterUiKey(cat.key, item.key)
                        ]
                      "
                      @input="onFilterInput($event, item, cat.key)"
                      multiple
                      :close-on-select="false"
                    />

                    <master-treeselect
                      :allowSelectingDisabledDescendants="true"
                      v-else-if="item.type === 'tree-select'"
                      :options="filtersOptions[`${item.list_route}`]"
                      multiple
                      :placeholder="item.name"
                      :value="getCategoryFilterValue(cat.key, item.key)"
                      @input="onFilterInput($event, item, cat.key)"
                      class="modern-tree-select"
                    />

                    <v-select
                      v-else-if="item.type === 'boolean'"
                      label="name"
                      :options="booleanOptions"
                      v-model="
                        columnOptions.filtersUI[
                          getCategoryFilterUiKey(cat.key, item.key)
                        ]
                      "
                      @input="onFilterInput($event, item, cat.key)"
                      :clearable="true"
                    />

                    <flat-pickr
                      v-else-if="item.type === 'date-range'"
                      :config="{ mode: 'range', ...$flatPickrConfig }"
                      :placeholder="item.name"
                      class="form-control modern-input"
                      :value="getCategoryFilterValue(cat.key, item.key)"
                      @input="onFilterInput($event, item, cat.key)"
                    />

                    <flat-pickr
                      v-else-if="item.type === 'year-range'"
                      :config="{
                        mode: 'range',
                        dateFormat: 'Y',
                        ...$flatPickrConfig,
                      }"
                      :placeholder="item.name"
                      class="form-control modern-input"
                      :value="getCategoryFilterValue(cat.key, item.key)"
                      @input="onFilterInput($event, item, cat.key)"
                    />

                    <b-form-input
                      v-else
                      :placeholder="item.type"
                      :value="getCategoryFilterValue(cat.key, item.key)"
                      @input="onFilterInput($event, item, cat.key)"
                      :type="item.type"
                      class="modern-input"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </template>

          <b-card
            v-else-if="
              selectedColSchema.filters && selectedColSchema.filters.length
            "
            class="section-card mb-2"
          >
            <div class="section-header mb-1">
              <feather-icon icon="FilterIcon" size="16" class="mr-1" />
              <strong>Filters</strong>
            </div>
            <b-row>
              <b-col
                v-for="(item, index) in selectedColSchema.filters"
                :key="'filter_input' + index"
                cols="12"
                md="4"
                class="mb-1"
              >
                <b-form-group
                  :label="item.name"
                  label-class="form-label"
                  class="mb-0"
                >
                  <v-select
                    v-if="item.type === 'select'"
                    label="name"
                    :options="filtersOptions[item.list_route]"
                    v-model="columnOptions.filtersUI[item.key]"
                    @input="onFilterInput($event, item)"
                  />

                  <v-select
                    v-else-if="item.type === 'multi-select'"
                    label="name"
                    :options="filtersOptions[item.list_route]"
                    v-model="columnOptions.filtersUI[item.key]"
                    @input="onFilterInput($event, item)"
                    multiple
                    :close-on-select="false"
                  />

                  <master-treeselect
                    :allowSelectingDisabledDescendants="true"
                    v-else-if="item.type === 'tree-select'"
                    :options="filtersOptions[`${item.list_route}`]"
                    multiple
                    :placeholder="item.name"
                    :value="columnOptions.filtersValues[`${item.key}`]"
                    @input="onFilterInput($event, item)"
                    class="modern-tree-select"
                  />

                  <v-select
                    v-else-if="item.type === 'boolean'"
                    label="name"
                    :options="booleanOptions"
                    v-model="columnOptions.filtersUI[item.key]"
                    @input="onFilterInput($event, item)"
                    :clearable="true"
                  />

                  <flat-pickr
                    v-else-if="item.type === 'date-range'"
                    :config="{ mode: 'range', ...$flatPickrConfig }"
                    :placeholder="item.name"
                    class="form-control modern-input"
                    :value="columnOptions.filtersValues[item.key]"
                    @input="onFilterInput($event, item)"
                  />

                  <flat-pickr
                    v-else-if="item.type === 'year-range'"
                    :config="{
                      mode: 'range',
                      dateFormat: 'Y',
                      ...$flatPickrConfig,
                    }"
                    :placeholder="item.name"
                    class="form-control modern-input"
                    :value="columnOptions.filtersValues[item.key]"
                    @input="onFilterInput($event, item)"
                  />

                  <b-form-input
                    v-else
                    :placeholder="item.type"
                    :value="columnOptions.filtersValues[`${item.key}`]"
                    @input="onFilterInput($event, item)"
                    :type="item.type"
                    class="modern-input"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <b-card v-if="pivotColOptions.length" class="section-card mb-1">
            <div class="section-header mb-1">
              <feather-icon icon="GridIcon" size="16" class="mr-1" />
              <strong>Pivoting</strong>
            </div>
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group label="Pivot Columns" label-class="form-label">
                  <v-select
                    v-model="columnOptions.pivotCol"
                    label="name"
                    :options="pivotColOptions"
                    class="modern-select"
                  >
                    <template #selected-option="data">
                      <span>{{ humanize(data.name) }}</span>
                    </template>
                    <template #option="data">
                      <span>{{ humanize(data.name) }}</span>
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Pivot Rows" label-class="form-label">
                  <v-select
                    v-model="columnOptions.pivotRow"
                    label="name"
                    :options="pivotRowOptions"
                    class="modern-select"
                  >
                    <template #selected-option="data">
                      <span>{{ humanize(data.name) }}</span>
                    </template>
                    <template #option="data">
                      <span>{{ humanize(data.name) }}</span>
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <b-card
            v-if="optionalSubColumns.length && selectedColSchema.key"
            class="section-card mb-1"
          >
            <div class="section-header mb-2">
              <feather-icon icon="ColumnsIcon" size="16" class="mr-1" />
              <strong>Sub-columns</strong>
            </div>
            <b-form-checkbox
              v-for="opt in optionalSubColumns"
              :key="'optional_col_' + opt.selectionKey"
              v-model="columnOptions.optionalSelections[opt.selectionKey]"
              class="mb-1"
            >
              {{ opt.label }}
            </b-form-checkbox>
          </b-card>

          <div class="d-flex justify-content-end mt-2">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-1"
              @click="resetColumnFilters"
            >
              Reset Filters
            </b-button>

            <b-button type="submit" variant="primary" size="sm" class="px-3">
              Save Configuration
            </b-button>
          </div>
        </b-card>
      </b-form>
    </b-modal>

    <!-- Preview Modal -->
    <b-modal
      ref="dataPreviewModalRef"
      title="Preview"
      size="xl"
      hide-footer
      @hidden="onPreviewModalHidden"
    >
      <b-card>
        <div
          v-if="preview.metadata.length"
          class="preview-metadata mb-2"
        >
          <div
            v-for="(line, m_i) in preview.metadata"
            :key="'preview_metadata_' + m_i"
            class="preview-metadata-line text-muted small"
          >
            {{ line }}
          </div>
        </div>
        <div class="table-container shadow-sm border">
          <table class="table preview-table">
            <tr class="bg-primary text-white">
              <th>Index</th>
              <th
                v-for="(h, h_i) in preview.headings"
                :key="'preview_heading_' + h_i"
              >
                {{ h }}
              </th>
            </tr>
            <tr v-for="(row, r_i) in preview.rows" :key="'preview_row_' + r_i">
              <td>{{ r_i + 1 }}</td>
              <td v-for="(d, d_i) in row" :key="'preview_data' + d_i">
                {{ d }}
              </td>
            </tr>
          </table>
        </div>

        <template #footer>
          <div>
            <b-row>
              <b-col
                cols="12"
                class="d-flex align-items-end justify-content-end justify-content-sm-center"
              >
                <b-pagination
                  v-model="pagination.currentPage"
                  :total-rows="pagination.totalRows"
                  :per-page="pagination.rowsPerPage"
                  @change="onPaginationChange"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                  :limit="15"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>

                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </template>
      </b-card>
    </b-modal>

    <!-- Top Configuration Card -->
    <b-card class="header-card">
      <b-row class="align-items-end">
        <b-col cols="12" md="4" class="mb-md-0">
          <b-form-group label="Report Type" label-class="form-label">
            <v-select
              placeholder="Select report type"
              label="name"
              :reduce="(i) => i.key"
              :options="objects"
              :value="objectKey"
              @input="onObjectSelect"
              class="modern-select"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4" class="mb-md-0">
          <b-form-group label="Saved Format" label-class="form-label">
            <v-select
              :disabled="!objectKey"
              label="name"
              placeholder="Select saved format (optional)"
              :reduce="(i) => i.id"
              :options="reportFormats"
              :value="reportFormatId"
              @input="onReportFormatSelected"
              class="modern-select"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-card
      v-if="objectKey === 'movies' && objectFiltersDef.length"
      class="header-card mt-2"
    >
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div>
          <h6 class="mb-0 d-flex align-items-center">
            <feather-icon icon="FilterIcon" size="16" class="mr-1" />
            Rights Availability
          </h6>
          <small class="text-muted"
            >Filter which movies appear in this report</small
          >
        </div>
        <div class="d-flex align-items-center">
          <b-form-checkbox
            v-model="showRightsAvailabilityFilters"
            switch
            class="mr-2 mb-0"
            @change="onShowRightsAvailabilityFiltersChange"
          >
            Show filter
          </b-form-checkbox>
          <b-button
            v-if="showRightsAvailabilityFilters"
            size="sm"
            variant="outline-secondary"
            @click="resetMoviesObjectFilters"
          >
            Reset Filters
          </b-button>
        </div>
      </div>
      <b-row v-if="showRightsAvailabilityFilters">
        <b-col
          v-for="(item, index) in objectFiltersDef"
          :key="'movies_object_filter_' + index"
          cols="12"
          md="4"
          lg="3"
          class="mb-1"
        >
          <b-form-group
            :label="item.name"
            label-class="form-label"
            class="mb-0"
          >
            <v-select
              v-if="item.type === 'multi-select'"
              label="name"
              :options="objectFiltersOptions[item.list_route]"
              v-model="moviesObjectFilters.ui[item.key]"
              @input="onMoviesObjectFilterInput($event, item)"
              multiple
              :close-on-select="false"
              class="modern-select"
            />

            <master-treeselect
              v-else-if="item.type === 'tree-select'"
              :allowSelectingDisabledDescendants="true"
              :options="objectFiltersOptions[item.list_route]"
              multiple
              :placeholder="item.name"
              :value="moviesObjectFilters.values[item.key]"
              @input="onMoviesObjectFilterInput($event, item)"
              class="modern-tree-select"
            />

            <v-select
              v-else-if="item.type === 'boolean'"
              label="name"
              :options="booleanOptions"
              v-model="moviesObjectFilters.ui[item.key]"
              @input="onMoviesObjectFilterInput($event, item)"
              :clearable="true"
              class="modern-select"
            />

            <flat-pickr
              v-else-if="item.type === 'date'"
              :config="{ ...$flatPickrConfig }"
              :placeholder="item.name"
              class="form-control"
              v-model="moviesObjectFilters.values[item.key]"
              @input="onMoviesObjectFilterInput($event, item)"
            />

            <b-form-input
              v-else
              :placeholder="item.type"
              :value="moviesObjectFilters.values[item.key]"
              @input="onMoviesObjectFilterInput($event, item)"
              :type="item.type"
              class="modern-input"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Resizable Panels Container -->
    <div class="resizable-container-wrapper" v-if="objectKey">
      <div
        class="resizable-container"
        :class="{ resizing: isResizing }"
        ref="resizeContainer"
      >
        <!-- Left Panel: Available Labels -->
        <div
          class="resizable-panel resizable-panel-left"
          :style="leftPanelStyle"
        >
          <b-card class="labels-card h-100">
            <div class="card-header-section mb-2">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <h6 class="mb-0 d-flex align-items-center">
                    <feather-icon icon="TagIcon" size="16" class="mr-1" />
                    Available Labels
                    <b-badge pill variant="light" class="ml-1 count-badge">
                      {{ objectSchema.length }}
                    </b-badge>
                  </h6>
                  <small class="text-muted"
                    >Click or drag to add to output</small
                  >
                </div>
              </div>
            </div>

            <b-form-group
              label="Search labels"
              label-class="form-label-sm mb-1"
            >
              <b-form-input
                placeholder="Type to filter labels"
                @input="onSearchObjectSchema"
                @focus="duplicateObjectSchema('copyOfObejectSchema')"
                class="search-input"
              />
            </b-form-group>

            <div class="labels-container">
              <draggable
                v-bind="dragOptions"
                v-model="objectSchema"
                class="labels-draggable"
                :list="objectSchema"
                group="people"
              >
                <div
                  class="label-item"
                  v-for="(element, index) in objectSchema"
                  :key="element.name + 'from' + index"
                  @click="() => handleLabelClick(element)"
                >
                  <span class="label-text">{{ element.name }}</span>
                  <b-badge
                    v-if="element.showOptions"
                    variant="warning"
                    class="filter-badge ml-1"
                    >F</b-badge
                  >
                </div>
                <div v-if="!objectSchema.length" class="empty-state">
                  <small class="text-muted">All labels are in output</small>
                </div>
              </draggable>
            </div>
          </b-card>
        </div>

        <!-- Resizer Handle -->
        <div
          class="resizer-handle"
          :class="{ resizing: isResizing }"
          @mousedown.prevent="startResize"
          @touchstart.prevent="startResize"
          title="Drag to resize panels"
        >
          <div class="resizer-icon">
            <div class="resizer-dots"></div>
            <div class="resizer-dots"></div>
            <div class="resizer-dots"></div>
          </div>
        </div>

        <!-- Right Panel: Output Labels -->
        <div
          class="resizable-panel resizable-panel-right"
          :style="rightPanelStyle"
        >
          <b-card class="output-card h-100">
            <div class="card-header-section mb-2">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <h6 class="mb-0 d-flex align-items-center">
                    <feather-icon icon="ListIcon" size="16" class="mr-1" />
                    Output Labels
                    <b-badge pill variant="dark" class="ml-1 count-badge">
                      {{ columns.length }}
                    </b-badge>
                  </h6>
                  <small class="text-muted"
                    >Drag out or click X to remove</small
                  >
                </div>
                <div v-if="columns && columns.length" class="action-buttons">
                  <b-button
                    size="sm"
                    variant="primary"
                    class="mr-1 action-btn"
                    @click="generateReport"
                  >
                    <feather-icon icon="DownloadIcon" size="14" class="mr-1" />
                    Download Excel
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="action-btn"
                    @click="openSaveReportModal"
                  >
                    <feather-icon icon="SaveIcon" size="14" class="mr-1" />
                    Save Format
                  </b-button>
                </div>
              </div>
            </div>

            <div class="output-labels-container">
              <draggable
                v-bind="dragOptions"
                v-model="columns"
                class="output-draggable"
                :list="columns"
                group="people"
              >
                <div
                  class="output-item"
                  v-for="(element, index) in columns"
                  :key="element.name + 'to' + index"
                  @click="openColumnsOptionsModal(element, index)"
                >
                  <div class="output-item-header">
                    <div class="output-item-title-wrapper">
                      <!-- <b-badge
                        variant="primary"
                        class="column-number-badge mr-1"
                      >
                        Col {{ index + 1 }}
                      </b-badge> -->
                      <span class="output-item-name">{{ element.name }}</span>
                      <!-- <b-badge
                        v-if="
                          element.showOptions && element.showOptions === true
                        "
                        variant="light"
                        class="filter-indicator ml-1"
                        >Filters</b-badge
                      > -->
                    </div>
                    <div class="output-item-actions">
                      <b-button
                        v-if="shouldShowFilterButton(element)"
                        size="sm"
                        variant="outline-secondary"
                        class="filter-config-btn mr-1"
                        title="Filters"
                        @click.stop="openColumnsOptionsModal(element, index)"
                      >
                        <feather-icon icon="FilterIcon" size="12" />
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        class="remove-btn"
                        @click.stop="
                          (event) => handleColumnClick(event, element)
                        "
                      >
                        <feather-icon icon="XIcon" size="12" />
                      </b-button>
                    </div>
                  </div>

                  <div
                    class="output-item-content"
                    v-if="
                      element.filtersText ||
                      element.pivotCol ||
                      element.pivotRow
                    "
                  >
                    <div
                      v-if="
                        Object.keys(
                          getNotEmptyFilters(element.filtersText || {}),
                        ).length
                      "
                      class="filters-section"
                    >
                      <small class="section-label">Filters:</small>
                      <div class="filter-tags">
                        <b-badge
                          v-for="(value, key) in getNotEmptyFilters(
                            element.filtersText,
                          )"
                          :key="'filter_value' + value + key"
                          variant="light"
                          class="filter-tag"
                        >
                          <span
                            class="filter-tag-text"
                            :title="getFilterName(key, element) + ': ' + value"
                          >
                            {{ getFilterName(key, element) }}: {{ value }}
                          </span>
                          <feather-icon
                            icon="XIcon"
                            size="10"
                            class="filter-remove ml-1"
                            @click.stop="(e) => removeFilter(e, element, key)"
                          />
                        </b-badge>
                      </div>
                    </div>

                    <div
                      v-if="element.pivotCol || element.pivotRow"
                      class="pivot-section"
                    >
                      <small class="section-label">Pivoting:</small>
                      <div class="pivot-badges">
                        <b-badge
                          v-if="element.pivotCol"
                          variant="light"
                          class="pivot-badge"
                        >
                          Column: {{ humanize(element.pivotCol.name) }}
                        </b-badge>
                        <b-badge
                          v-if="element.pivotRow"
                          variant="light"
                          class="pivot-badge"
                        >
                          Row: {{ humanize(element.pivotRow.name) }}
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="!columns.length" class="empty-state">
                  <small class="text-muted">
                    Drag labels from the left to build your output
                  </small>
                </div>
              </draggable>
            </div>
          </b-card>
        </div>
      </div>
    </div>

    <!-- Empty State when no report type selected -->
    <b-card v-if="!objectKey" class="empty-state-card mt-4">
      <div class="empty-state-content text-center py-5">
        <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
        <h5 class="text-muted">Select a Report Type</h5>
        <p class="text-muted mb-0">
          Choose a report type from above to start building your report
        </p>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormSelect,
  BFormSelectOption,
  BFormCheckbox,
  BContainer,
  BRow,
  BCol,
  BDropdown,
  BDropdownItem,
  BIcon,
  BIconPlus,
  BIconDash,
  BInputGroup,
  BFormInput,
  BIconTypeBold,
  BIconTypeItalic,
  BIconTypeStrikethrough,
  BBadge,
  BButton,
  BForm,
  BFormGroup,
  BPagination,
  VBTooltip,
  BProgress,
} from "bootstrap-vue";
import draggable from "vuedraggable";
import * as ReportBuilderServices from "@/apiServices/ReportBuilderServices";
import vSelect from "vue-select";
import { downloadFromURL, getTreeLabelsById } from "@/utils/helpers";
import ToastificationContentVue from "@/@core/components/toastification/ToastificationContent.vue";
import { humanize } from "@/utils/helpers";
import { getMasterList } from "@/apiServices/MastersServices";
import { TokenService } from "@/apiServices/storageService";
import { reportAbort } from "./reportAbort";
import flatPickr from "vue-flatpickr-component";

export default {
  components: {
    BCard,
    BProgress,
    BFormSelect,
    BContainer,
    BFormSelectOption,
    draggable,
    BFormCheckbox,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    BIcon,
    BIconPlus,
    BIconDash,
    BInputGroup,
    BFormInput,
    BIconTypeBold,
    BIconTypeItalic,
    BIconTypeStrikethrough,
    BBadge,
    vSelect,
    flatPickr,
    BButton,
    BForm,
    BFormGroup,
    BPagination,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      // Generate Report
      // downloading: false,
      // controller: null,
      // isMinimized: false,
      reader: null,
      percent: 0,
      isCancelling: false,
      isClosingProgrammatically: false,
      invalidReport: false,
      objectKey: "",
      objectSchema: [],
      copyOfObejectSchema: [],
      objectSchemaMain: [],
      objects: [],
      columns: [],
      reportFormats: [],
      reportFormatId: "",
      reportFormatName: "",
      selectedCol: {},
      selectedColIndex: "",
      columnOptionsModalKey: 0,
      booleanOptions: [
        { name: "Yes", value: true },
        { name: "No", value: false },
      ],
      columnOptions: {
        width: 10,
        filtersUI: {},
        filtersValues: {
          columns: [],
        },
        filtersText: {},
        optionalSelections: {},
        categorySelections: {},
        pivotCol: "",
        pivotRow: "",
        columns: [],
        columnsKeys: [],
      },
      filtersOptions: {},
      objectFiltersDef: [],
      objectFiltersOptions: {},
      moviesObjectFilters: {
        values: {},
        text: {},
        ui: {},
      },
      moviesObjectFiltersStorageKey: "reportBuilderExcel_movies_objectFilters",
      showRightsAvailabilityFilters: false,
      preview: {
        metadata: [],
        headings: [],
        rows: [],
      },
      pagination: {
        currentPage: 1,
        totalRows: 1,
        rowsPerPage: 10,
        perPageOptions: [25, 50, 100],
      },
      // Resizer state
      leftPanelWidth: 50, // percentage
      isResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 50,
      // Report generation timing
      reportStartTime: null,
    };
  },

  watch: {
    columns() {
      if (this.columns && this.columns.length > 1) {
        const keys = this.columns.map((obj) => obj.key);

        const hasTRL = keys.includes("trl_availability");
        const hasOpenTRL = keys.includes("movie_open_trls");

        if (hasTRL && hasOpenTRL) {
          alert("Open Rights and Rights Availability together is not allowed");

          // Get the last-added item before removing
          const lastItem = this.columns[this.columns.length - 1];

          // Remove it if it's one of the conflicting types
          if (["trl_availability", "movie_open_trls"].includes(lastItem.key)) {
            this.columns.pop(); // remove from columns
            this.objectSchema.push(lastItem); // push it back to objectSchema
          }
        }
      }
    },
    "$store.state.report._internalAbort"() {
      this.abortReport();
    },
    "columnOptions.categorySelections": {
      deep: true,
      handler() {
        this.syncPivotSelectionsWithOptions();
      },
    },
    "columnOptions.optionalSelections": {
      deep: true,
      handler() {
        this.syncPivotSelectionsWithOptions();
      },
    },
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost",
      };
    },
    leftPanelStyle() {
      return {
        width: this.leftPanelWidth + "%",
      };
    },
    rightPanelStyle() {
      return {
        width: 100 - this.leftPanelWidth + "%",
      };
    },
    optionalSubColumns() {
      const schemaCol = this.selectedColSchema;
      const options = this.getOptionalSubColumns(this.selectedCol);

      if (!schemaCol?.categories?.length) {
        return options;
      }

      return options.filter(
        (opt) => this.columnOptions.categorySelections[opt.category] !== false,
      );
    },
    selectedColSchema() {
      return this.getSchemaColumnDef(this.selectedCol);
    },
    pivotColOptions() {
      if (!this.selectedColSchema?.key) {
        return [];
      }

      return this.buildActiveSubColumns(
        this.selectedCol,
        this.columnOptions.optionalSelections,
        this.columnOptions.categorySelections,
      ).filter((item) => item.p_col);
    },
    pivotRowOptions() {
      if (!this.selectedColSchema?.key) {
        return [];
      }

      return this.buildActiveSubColumns(
        this.selectedCol,
        this.columnOptions.optionalSelections,
        this.columnOptions.categorySelections,
      ).filter((item) => item.p_row);
    },
    activeSynopsisCategories() {
      const schemaCol = this.selectedColSchema;
      if (!schemaCol?.categories?.length) {
        return [];
      }

      return schemaCol.categories.filter((cat) => {
        if (!cat.optional) {
          return true;
        }
        return this.columnOptions.categorySelections[cat.key] !== false;
      });
    },
  },
  methods: {
    getSchemaColumnDef(col) {
      if (!col || !col.key) return col;
      return this.objectSchemaMain.find((item) => item.key === col.key) || col;
    },
    shouldShowFilterButton(col) {
      const schemaCol = this.getSchemaColumnDef(col);
      if (!schemaCol) {
        return false;
      }

      return !!(
        schemaCol.categories?.length ||
        schemaCol.filters?.length ||
        schemaCol.columnOption ||
        (schemaCol.columns || []).some(
          (item) => item.optional || item.p_col || item.p_row,
        )
      );
    },
    syncPivotSelectionsWithOptions() {
      const colNames = new Set(this.pivotColOptions.map((item) => item.name));
      const rowNames = new Set(this.pivotRowOptions.map((item) => item.name));

      if (
        this.columnOptions.pivotCol &&
        !colNames.has(this.columnOptions.pivotCol.name)
      ) {
        this.columnOptions.pivotCol = null;
      }

      if (
        this.columnOptions.pivotRow &&
        !rowNames.has(this.columnOptions.pivotRow.name)
      ) {
        this.columnOptions.pivotRow = null;
      }
    },
    getOptionalSubColumns(col) {
      const schemaCol = this.getSchemaColumnDef(col);

      if (schemaCol?.categories?.length) {
        const optionalColumns = [];

        for (const cat of schemaCol.categories) {
          for (const item of cat.columns || []) {
            if (!item.optional) {
              continue;
            }

            optionalColumns.push({
              ...item,
              category: cat.key,
              selectionKey: `${cat.key}_${item.name}`,
              label: `${cat.name} ${this.humanize(item.name)}`,
            });
          }
        }

        return optionalColumns;
      }

      return (schemaCol?.columns || [])
        .filter((item) => item.optional)
        .map((item) => ({
          ...item,
          selectionKey: item.name,
          label: this.humanize(item.name),
        }));
    },
    getDefaultCategorySelections(col) {
      const schemaCol = this.getSchemaColumnDef(col);
      const selections = {};

      for (const cat of schemaCol?.categories || []) {
        if (cat.optional) {
          selections[cat.key] = true;
        }
      }

      return selections;
    },
    getCategoryFilterUiKey(categoryKey, filterKey) {
      return `${categoryKey}_${filterKey}`;
    },
    getCategoryFilterValue(categoryKey, filterKey) {
      const categoryFilters = this.columnOptions.filtersValues[categoryKey];
      return categoryFilters ? categoryFilters[filterKey] : null;
    },
    getDefaultOptionalSelections(col) {
      const schemaCol = this.getSchemaColumnDef(col);
      const activeColumns = col?.columns || this.buildActiveSubColumns(col);
      const selections = {};

      for (const opt of this.getOptionalSubColumns(col)) {
        selections[opt.selectionKey] = activeColumns.some(
          (item) =>
            item.name === `${opt.category}_${opt.name}` ||
            (item.name === opt.name && item.category === opt.category),
        );
      }

      return selections;
    },
    buildActiveSubColumns(
      col,
      optionalSelections = {},
      categorySelections = {},
    ) {
      const schemaCol = this.getSchemaColumnDef(col);

      if (schemaCol?.categories?.length) {
        const resolvedCategorySelections = Object.keys(categorySelections)
          .length
          ? categorySelections
          : col?.filtersValues?.categorySelections ||
            this.getDefaultCategorySelections(col);

        const activeCategories = schemaCol.categories.filter((cat) => {
          if (cat.optional) {
            return resolvedCategorySelections[cat.key] !== false;
          }
          return true;
        });

        return activeCategories.flatMap((cat) =>
          (cat.columns || [])
            .filter((item) => {
              if (!item.optional) {
                return true;
              }
              return optionalSelections[`${cat.key}_${item.name}`] !== false;
            })
            .map((item) => ({
              ...item,
              category: cat.key,
              name: `${cat.key}_${item.name}`,
            })),
        );
      }

      const baseColumns = schemaCol?.columns || [];

      return baseColumns
        .filter((item) => {
          if (item.optional) {
            return optionalSelections[item.name] !== false;
          }
          return true;
        })
        .map((item) => ({
          ...item,
          selectionKey: item.name,
        }));
    },
    mapSubColumnsWithWidth(columns = []) {
      return columns.map((sub) => ({
        ...sub,
        width: sub.width || this.getHeaderWidth(sub.name),
      }));
    },
    resetColumnFilters() {
      this.columnOptions.filtersValues = {};
      this.columnOptions.filtersText = {};
      this.columnOptions.filtersUI = {};
      this.columnOptions.categorySelections = this.getDefaultCategorySelections(
        this.selectedCol,
      );
      this.columnOptions.optionalSelections = this.getDefaultOptionalSelections(
        this.selectedCol,
      );
    },
    getBooleanValue(key) {
      const val = this.columnOptions.filtersValues[key];
      if (val === true) return { name: "Yes", value: true };
      if (val === false) return { name: "No", value: false };
      return null;
    },
    getMultiSelectValue(item) {
      const ids = this.columnOptions.filtersValues[item.key];
      if (!Array.isArray(ids)) return [];

      return (this.filtersOptions[item.list_route] || []).filter((opt) =>
        ids.includes(opt.id),
      );
    },
    getHeaderWidth(name) {
      if (!name) return 15;
      return Math.max(name.length + 5, 15);
    },
    onItemAdded(event) {
      console.log("Item added:", event.item); // The actual DOM element
      console.log("Added element:", this.objectSchema[event.newIndex]); // The data object
    },
    getNotEmptyFilters(filters) {
      const nonEmptyFilters = {};

      for (const key in filters) {
        if (
          filters[key] === "" ||
          filters[key] === null ||
          (Array.isArray(filters[key]) && filters[key].length === 0)
        ) {
          continue;
        }
        nonEmptyFilters[key] = filters[key];
      }

      return nonEmptyFilters;
    },
    getFilterName(key, element) {
      for (const f of element.filters) {
        if (f.key === key) {
          return f.name;
        }
      }
      return "";
    },
    // removeFilter(event, element, key) {
    //   event.stopPropagation();
    //   if (element.filtersValues[key]) {
    //     delete element.filtersValues[key];
    //     delete element.filtersText[key];
    //   }
    //   this.$forceUpdate();
    // },
    // removeFilter(event, element, key) {
    //   event.stopPropagation();

    //   // Remove the filter from both filtersValues and filtersText
    //   if (element.filtersValues[key]) {
    //     delete element.filtersValues[key];
    //     delete element.filtersText[key];
    //   }

    //   // Sort columns alphabetically by name after filter removal
    //   this.objectSchema = objectSchema.sort((a, b) =>
    //     a.name.localeCompare(b.name)
    //   );
    //   this.objectSchemaMain = objectSchema;

    //   this.$forceUpdate(); // Force re-render if needed
    // },

    removeFilter(event, element, key) {
      event.stopPropagation();

      // Vue-safe deletes
      this.$delete(element.filtersValues, key);
      this.$delete(element.filtersText, key);

      // force refresh (only needed for deep nested objects)
      this.columns = [...this.columns];
    },

    abortReport() {
      if (reportAbort.controller) {
        reportAbort.controller.abort();
        reportAbort.controller = null;
      }

      // Cleanup local refs
      this.reader = null;
      this.controller = null;
      this.reportStartTime = null;
    },

    onFilterInput(i, item, categoryKey = null) {
      const valuesTarget = categoryKey
        ? this.columnOptions.filtersValues[categoryKey] ||
          this.$set(this.columnOptions.filtersValues, categoryKey, {})
        : this.columnOptions.filtersValues;
      const textTarget = categoryKey
        ? this.columnOptions.filtersText[categoryKey] ||
          this.$set(this.columnOptions.filtersText, categoryKey, {})
        : this.columnOptions.filtersText;

      if (item.type === "multi-select") {
        valuesTarget[item.key] = i.map((x) => x.id);
        textTarget[item.key] = i.map((x) => x.name).join(", ");
      } else if (item.type === "select") {
        valuesTarget[item.key] = i.id;
        textTarget[item.key] = i.name;
      } else if (item.type === "boolean") {
        if (!i) {
          this.$delete(valuesTarget, item.key);
          this.$delete(textTarget, item.key);
          return;
        }
        valuesTarget[item.key] = i.value;
        textTarget[item.key] = i.name;
      } else if (item.type === "tree-select") {
        valuesTarget[item.key] = i;
        textTarget[item.key] = getTreeLabelsById(
          i,
          this.filtersOptions[item.list_route],
        );
      } else if (item.type === "date-range" || item.type === "year-range") {
        valuesTarget[item.key] = i;
        textTarget[item.key] = i || "";
      } else {
        valuesTarget[item.key] = i;
        textTarget[item.key] = i;
      }
    },

    getMultipleFilterName(filterOptions, keys) {
      try {
        if (!filterOptions) {
          return;
        }

        let nameArray = [];

        for (let key of keys) {
          let item;

          for (let filterOption of filterOptions) {
            let found = false;
            if (filterOption.key == key) {
              item = filterOption;
              found = true;
            }
            if (filterOption.children.length > 0) {
              for (let childFilterOption of filterOption.children) {
                if (childFilterOption.key == key) {
                  item = childFilterOption;
                  found = true;
                }
              }
            }
            if (found) {
              break;
            }
          }
          if (item) {
            nameArray.push(item.label);
          }
        }

        if (nameArray.length == 0) return "";
        return nameArray.toString().split(",").join(", ");
      } catch (error) {
        console.error(`Error in getMultipleFilterName `, error);
      }
    },

    handleLabelClick(element) {
      const schemaCol = this.getSchemaColumnDef(element);

      // NORMAL COLUMN
      if (!schemaCol.columns && !schemaCol.categories) {
        this.columns.push({
          ...element,
          width: this.getHeaderWidth(element.name),
        });
      }
      // JSON COLUMN
      else {
        const defaultCategorySelections =
          this.getDefaultCategorySelections(element);
        const activeColumns = this.buildActiveSubColumns(
          element,
          {},
          defaultCategorySelections,
        );
        this.columns.push({
          ...element,
          columns: this.mapSubColumnsWithWidth(activeColumns),
          filtersValues: {
            categorySelections: defaultCategorySelections,
            columns: activeColumns,
          },
        });
      }

      this.objectSchema = this.objectSchema.filter(
        (object) => object.key !== element.key,
      );
    },

    handleColumnClick(event, element) {
      event.stopPropagation();
      this.objectSchema.push(element);
      this.objectSchema.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
      this.columns = this.columns.filter(
        (object) => object.key !== element.key,
      );
    },

    humanize,
    onPaginationChange() {
      this.$nextTick(() => {
        this.previewExcelData();
      });
    },
    onPreviewModalHidden() {
      this.preview.metadata = [];
      this.pagination = {
        currentPage: 1,
        totalRows: 1,
        rowsPerPage: 10,
        perPageOptions: [25, 50, 100],
      };
    },
    async previewExcelData() {
      try {
        const response = await ReportBuilderServices.GetPreviewReportExcel({
          key: this.objectKey,
          columns: this.columns,
          object_filters: this.getMoviesObjectFiltersPayload(),
          object_filters_text: this.getMoviesObjectFiltersText(),
          object_filters_parts: this.getMoviesObjectFilterParts(),
          limit: this.pagination.rowsPerPage,
          offset:
            (this.pagination.currentPage - 1) * this.pagination.rowsPerPage,
          preview: true,
        });

        if (!response.data.status) {
          return;
        }

        const data = response.data.data;
        const rows = [...(data.rows || [])];
        const metadata = [];

        while (
          rows.length &&
          typeof rows[0]?.[0] === "string" &&
          (rows[0][0].startsWith("Rights availability:") ||
            rows[0][0].startsWith("Labels:"))
        ) {
          metadata.push(rows.shift()[0]);
        }

        this.preview.metadata = metadata;
        this.preview.headings = this.columns.map((col) =>
          this.getReportColumnHeader(col),
        );
        this.preview.rows = rows;

        this.pagination.totalRows = data.total;

        this.$refs["dataPreviewModalRef"].show();
      } catch (error) {
        console.error(`Error in download excel `, error);
      }
    },
    async openColumnsOptionsModal(col, index) {
      this.columnOptionsModalKey++;

      this.selectedCol = col;
      this.selectedColIndex = index;

      const schemaCol = this.getSchemaColumnDef(col);
      const defaultCategorySelections = col.filtersValues?.categorySelections
        ? { ...col.filtersValues.categorySelections }
        : this.getDefaultCategorySelections(col);
      const defaultOptionalSelections = this.getDefaultOptionalSelections(col);

      this.columnOptions = {
        width: col.width || 20,
        filtersValues: col.filtersValues ? { ...col.filtersValues } : {},
        filtersText: col.filtersText ? { ...col.filtersText } : {},
        filtersUI: {},
        optionalSelections: {
          ...defaultOptionalSelections,
          ...(col.filtersValues?.optionalSelections || {}),
        },
        categorySelections: { ...defaultCategorySelections },
        pivotCol: col.pivotCol || null,
        pivotRow: col.pivotRow || null,
      };

      let showModal = false;

      const filterDefs = schemaCol?.categories?.length
        ? schemaCol.categories.flatMap((cat) => cat.filters || [])
        : col.filters || [];

      if (filterDefs.length) {
        showModal = true;

        for (const f of filterDefs) {
          if (["select", "multi-select", "tree-select"].includes(f.type)) {
            if (this.filtersOptions[f.list_route]?.length) continue;

            this.$set(this.filtersOptions, f.list_route, []);

            const res = await getMasterList({ list_route: f.list_route });

            this.filtersOptions[f.list_route].splice(
              0,
              this.filtersOptions[f.list_route].length,
              ...res.data.data,
            );
          }
        }
      }

      if (schemaCol?.categories?.length) {
        showModal = true;

        for (const cat of schemaCol.categories) {
          for (const f of cat.filters || []) {
            const stored = this.columnOptions.filtersValues[cat.key]?.[f.key];
            if (stored == null) continue;

            let rebuiltValue = null;

            if (f.type === "multi-select") {
              rebuiltValue = (this.filtersOptions[f.list_route] || []).filter(
                (o) => stored.includes(o.id),
              );
            } else if (f.type === "boolean") {
              rebuiltValue =
                stored === true
                  ? this.booleanOptions.find((o) => o.value === true)
                  : this.booleanOptions.find((o) => o.value === false);
            } else if (f.type === "select") {
              rebuiltValue = (this.filtersOptions[f.list_route] || []).find(
                (o) => o.id === stored,
              );
            }

            this.$set(
              this.columnOptions.filtersUI,
              this.getCategoryFilterUiKey(cat.key, f.key),
              rebuiltValue,
            );
          }
        }
      } else {
        for (const f of col.filters || []) {
          const stored = this.columnOptions.filtersValues[f.key];
          if (stored == null) continue;

          let rebuiltValue = null;

          if (f.type === "multi-select") {
            rebuiltValue = (this.filtersOptions[f.list_route] || []).filter(
              (o) => stored.includes(o.id),
            );
          } else if (f.type === "boolean") {
            rebuiltValue =
              stored === true
                ? this.booleanOptions.find((o) => o.value === true)
                : this.booleanOptions.find((o) => o.value === false);
          } else if (f.type === "select") {
            rebuiltValue = (this.filtersOptions[f.list_route] || []).find(
              (o) => o.id === stored,
            );
          }

          this.$set(this.columnOptions.filtersUI, f.key, rebuiltValue);
        }
      }

      const activeColumns = this.buildActiveSubColumns(
        col,
        this.columnOptions.optionalSelections,
        this.columnOptions.categorySelections,
      );

      if (
        schemaCol?.categories?.length ||
        filterDefs.length ||
        this.getOptionalSubColumns(col).length ||
        activeColumns.some((item) => item.p_col || item.p_row)
      ) {
        showModal = true;
      }

      if (showModal) {
        this.$nextTick(() => {
          this.syncPivotSelectionsWithOptions();
        });
        this.$refs.columnOptionsRef.show();
      }
    },

    saveColumnOptions() {
      if (this.columnOptions.pivotCol) {
        if (!this.columnOptions.pivotRow) {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: "Select Row for pivoting",
              icon: "EditIcon",
              variant: "primary",
            },
          });
          return;
        }
        if (
          JSON.stringify(this.columnOptions.pivotCol) ===
          JSON.stringify(this.columnOptions.pivotRow)
        ) {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: "Column and Row cannot be same",
              icon: "EditIcon",
              variant: "primary",
            },
          });
          return;
        }
      }

      const activeColumns = this.buildActiveSubColumns(
        this.selectedCol,
        this.columnOptions.optionalSelections,
        this.columnOptions.categorySelections,
      );

      this.columns[this.selectedColIndex] = {
        ...this.columns[this.selectedColIndex],
        pivotCol: this.columnOptions.pivotCol,
        pivotRow: this.columnOptions.pivotRow,
        filtersValues: {
          ...this.columnOptions.filtersValues,
          categorySelections: { ...this.columnOptions.categorySelections },
          columns: activeColumns.length ? activeColumns : undefined,
        },
        filtersText: this.columnOptions.filtersText,
        columns: activeColumns.length
          ? this.mapSubColumnsWithWidth(activeColumns)
          : this.columns[this.selectedColIndex].columns,
      };

      this.selectedCol = "";
      this.selectedColIndex = 0;
      this.$refs["columnOptionsRef"].hide();
    },
    onSearchObjectSchema(i) {
      if (i) {
        this.objectSchema = this.copyOfObejectSchema.filter((d) => {
          const name = d.name;
          if (name && typeof name === "string") {
            return name.toLowerCase().includes(i.toLowerCase());
          }
        });
      } else {
        this.objectSchema = this.copyOfObejectSchema;
      }
    },
    duplicateObjectSchema(data) {
      if (this.columns && this.columns.length) {
        const result = this.objectSchemaMain.filter((i) => {
          for (const j of this.columns) {
            if (j.key === i.key) {
              return false;
            }
          }
          return true;
        });
        this[data] = result;
      } else {
        this[data] = [...this.objectSchemaMain];
      }
    },
    openSaveReportModal() {
      this.$refs["saveReportFormatForm"].show();
    },
    async onReportFormatSelected(id) {
      this.reportFormatId = id;
      if (!id) return;

      try {
        const res = await ReportBuilderServices.GetSingleFormats(id);
        const parsed = JSON.parse(res.data.data.format_text);

        this.columns = parsed.map((col) => {
          // normal column
          if (!col.columns) {
            return {
              ...col,
              width: col.width || this.getHeaderWidth(col.name),
            };
          }

          const activeColumns = col.columns.map((sub) => ({
            ...sub,
            width: sub.width || this.getHeaderWidth(sub.name),
          }));

          const filtersValues = {
            ...(col.filtersValues || {}),
            columns: col.filtersValues?.columns?.length
              ? col.filtersValues.columns
              : activeColumns,
          };

          if (!filtersValues.categorySelections) {
            const schemaCol = this.objectSchemaMain.find(
              (item) => item.key === col.key,
            );
            if (schemaCol?.categories?.length) {
              filtersValues.categorySelections =
                this.getDefaultCategorySelections(schemaCol);
            }
          }

          // json column
          return {
            ...col,
            columns: activeColumns,
            filtersValues,
          };
        });
      } catch (error) {
        console.error("Error in onReportFormatSelected", error);
      }
    },
    async saveReportFormat() {
      try {
        const res = await ReportBuilderServices.SaveFormat({
          id: this.reportFormatId,
          type: "EXCEL",
          name: this.reportFormatName,
          key: this.objectKey,
          format_text: JSON.stringify(this.columns),
        });
        if (res.data.status) {
          this.reportFormatId = res.data.data;
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: res.data.message || "Success",
              icon: "EditIcon",
              variant: "failure",
            },
          });
          this.getReportFormats(this.objectKey);
        }
      } catch (error) {
        console.error(`Error in saveReportFormat `, error);
      }
      this.$refs["saveReportFormatForm"].hide();
    },
    getEstimatedTimeRemaining() {
      if (
        !this.reportStartTime ||
        this.$store.state.report.percent === 0 ||
        this.$store.state.report.percent >= 100
      ) {
        return "Calculating...";
      }

      const elapsed = (Date.now() - this.reportStartTime) / 1000; // seconds
      const rate = this.$store.state.report.percent / elapsed; // percent per second
      const remaining = (100 - this.$store.state.report.percent) / rate; // seconds remaining
      if (remaining < 60) {
        return `${Math.round(remaining)} seconds`;
      } else if (remaining < 3600) {
        const minutes = Math.floor(remaining / 60);
        const seconds = Math.round(remaining % 60);
        return `${minutes}m ${seconds}s`;
      } else {
        const hours = Math.floor(remaining / 3600);
        const minutes = Math.round((remaining % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
    },
    async generateReportComplete(file_name) {
      try {
        this.isMinimized = false;
        // We are closing the modal programmatically on success
        this.isClosingProgrammatically = true;
        this.$store.commit("report/FINISH");
        this.$store.commit("report/UPDATE_PERCENT", 0);
        this.reportStartTime = null;
        this.controller = null;
        const reportName = this.reportFormatName || "report";

        const token = TokenService.getToken();
        const url = `${process.env.VUE_APP_BASEURL}/reportsBuilder/excel-download/${file_name}?authorization=${token}&report_name=${reportName}`;

        downloadFromURL(url, reportName, "xlsx");

        // Show a success toast instead of cancel confirmation
        this.$toast({
          component: ToastificationContentVue,
          props: {
            title: "Report generated successfully",
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        console.error(`Error in downloading excel `, error);
        alert("Error in downloading excel");
      }
    },
    async showReportAlreadyRunningInfo() {
      await this.$bvModal.msgBoxOk(
        "A report is already being generated.\nPlease cancel it to start a new one.",
        {
          title: "Report already being generated",
          size: "sm",
          okVariant: "secondary",
          okTitle: "Close", // 👈 single button
          centered: true,
          hideHeaderClose: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
        },
      );
    },
    async generateReport() {
      try {
        const token = TokenService.getToken();

        if (!token) {
          alert("Error in generating report");
          return;
        }

        if (this.$store.state.report.downloading) {
          await this.showReportAlreadyRunningInfo();
          return;
        }

        this.$store.commit("report/START");
        this.reader = null;
        this.isCancelling = false;

        reportAbort.controller = new AbortController();
        const { signal } = reportAbort.controller;

        const response = await fetch(
          `${process.env.VUE_APP_BASEURL}/reportsBuilder/excel?authorization=${token}`,
          {
            headers: {
              "Transfer-Encoding": "chunked",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              report_name: this.reportFormatName || "report",
              key: this.objectKey,
              columns: this.columns,
              object_filters: this.getMoviesObjectFiltersPayload(),
              object_filters_text: this.getMoviesObjectFiltersText(),
              object_filters_parts: this.getMoviesObjectFilterParts(),
            }),
            method: "POST",
            signal,
          },
        );

        this.reader = response.body.getReader();
        const reader = this.reader;

        try {
          while (true) {
            // Check if request was aborted
            if (signal.aborted) {
              await reader.cancel();
              break;
            }

            const data = await reader.read();

            // Check if stream is done
            if (data.done) {
              break;
            }

            // Check if request was aborted after read
            if (signal.aborted) {
              await reader.cancel();
              break;
            }

            const jsonStr = String.fromCharCode.apply(null, data.value);

            if (typeof jsonStr !== "string") {
              alert("Error");
              this.cancelGenerateReport();
              break;
            }

            if (!jsonStr.endsWith("}")) {
              console.error(`Chunks Error in reader`);
              console.log({ jsonStr });
              this.cancelGenerateReport();
              break;
            }

            let jsonStrArr;
            if (jsonStr.includes("}{")) {
              jsonStrArr = jsonStr.split("}{").join("}||{").split("||");
            } else {
              jsonStrArr = [jsonStr];
            }

            for (const str of jsonStrArr) {
              const json = JSON.parse(str);

              if (typeof json.status === "boolean" && !json.status) {
                alert(json.message);
                this.cancelGenerateReport();
                return;
              }

              if (json.error) {
                alert(json.message);
                this.cancelGenerateReport();
                return;
              }

              if (json.finished) {
                this.$store.commit("report/UPDATE_PERCENT", 100);
                this.generateReportComplete(json.file_name);
                return;
              }

              if (json.percent !== undefined) {
                this.$store.commit("report/UPDATE_PERCENT", json.percent);
              } else if (json.total && json.index !== undefined) {
                this.$store.commit(
                  "report/UPDATE_PERCENT",
                  parseFloat(((json.index * 100) / json.total).toFixed(1)),
                );
              }
              if (json.data) {
                this.data = json.data;
              }
            }
          }
        } finally {
          // Clean up reader
          if (this.reader) {
            try {
              await this.reader.cancel();
            } catch (e) {
              // Ignore cancel errors
            }
            this.reader = null;
          }
        }
      } catch (error) {
        // Check if the error is due to abort
        if (
          error.name === "AbortError" ||
          error.message === "The user aborted a request." ||
          signal.aborted
        ) {
          console.log("Report generation cancelled by user");
          // Clean up
          this.reader = null;
          this.controller = null;
          this.$store.commit("report/FINISH");
          this.$store.commit("report/UPDATE_PERCENT", 0);
          this.reportStartTime = null;
          return;
        }
        console.error("Error in generating report ", error);
        this.cancelGenerateReport();
      }
    },
    onObjectSelect(input) {
      this.clearAll();
      this.getObjectSchema(input);
      this.objectKey = input;
      this.getReportFormats(input);
    },
    async getObjectSchema(key) {
      try {
        const res = await ReportBuilderServices.GetObjectsSchema(key);
        const payload = res.data.data;
        const objectSchema = Array.isArray(payload) ? payload : payload.columns || [];
        this.objectFiltersDef = Array.isArray(payload)
          ? []
          : payload.object_filters || [];

        if (key === "movies") {
          await this.loadMoviesObjectFilterOptions();
          this.restoreMoviesObjectFiltersFromStorage();
          this.rebuildMoviesObjectFiltersUi();
          this.rebuildMoviesObjectFiltersText();
        } else {
          this.objectFiltersDef = [];
          this.resetMoviesObjectFilters(false);
        }

        if (this.columns && this.columns.length) {
          const filteredSchema = objectSchema.filter((i) => {
            for (const j of this.columns) {
              if (j.key === i.key) {
                return false;
              }
            }
            return true;
          });
          this.objectSchema = filteredSchema.sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          this.objectSchemaMain = objectSchema;
        } else {
          this.objectSchema = [...objectSchema].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          this.objectSchemaMain = objectSchema;
        }
      } catch (error) {
        console.error(`Error in getObjectSchema`, error);
      }
    },
    async getObjects() {
      try {
        const res = await ReportBuilderServices.GetObjects();
        this.objects = res.data.data;
      } catch (error) {
        console.error(`Error in getObjects `, error);
      }
    },
    async getReportFormats(objectKey) {
      try {
        const res = await ReportBuilderServices.GetAllFormats({
          type: "EXCEL",
          key: objectKey,
        });
        if (res.data.status) {
          this.reportFormats = res.data.data;
        }
      } catch (error) {
        console.error(`Error in getReportFormats`, error);
      }
    },
    clearAll() {
      this.reportFormatId = "";
      this.reportFormatName = "";
      this.columns = [];
      this.objectSchema = [];
      this.objectSchemaMain = [];
      this.objectFiltersDef = [];
      this.resetMoviesObjectFilters(false);
    },
    getMoviesObjectFiltersPayload() {
      if (
        this.objectKey !== "movies" ||
        !this.showRightsAvailabilityFilters
      ) {
        return undefined;
      }
      const filters = this.getNotEmptyFilters(this.moviesObjectFilters.values);
      return Object.keys(filters).length ? filters : undefined;
    },
    getMoviesObjectFilterDisplayValue(item) {
      const stored = this.moviesObjectFilters.values?.[item.key];
      if (
        stored == null ||
        stored === "" ||
        (Array.isArray(stored) && stored.length === 0)
      ) {
        return "";
      }

      if (item.type === "multi-select") {
        const options = this.objectFiltersOptions[item.list_route] || [];
        return options
          .filter((opt) => stored.includes(opt.id))
          .map((opt) => opt.name)
          .join(", ");
      }

      if (item.type === "boolean") {
        if (stored === true) {
          return "Yes";
        }
        if (stored === false) {
          return "No";
        }
        return "";
      }

      if (item.type === "tree-select") {
        const labels = getTreeLabelsById(
          stored,
          this.objectFiltersOptions[item.list_route] || [],
        );
        return labels || (Array.isArray(stored) ? stored.join(", ") : String(stored));
      }

      if (item.type === "date") {
        return String(stored);
      }

      return String(stored);
    },
    rebuildMoviesObjectFiltersText() {
      if (this.objectKey !== "movies") {
        return;
      }

      for (const item of this.objectFiltersDef) {
        const display = this.getMoviesObjectFilterDisplayValue(item);
        if (display) {
          this.$set(this.moviesObjectFilters.text, item.key, display);
        } else {
          this.$delete(this.moviesObjectFilters.text, item.key);
        }
      }
    },
    getMoviesObjectFiltersText() {
      if (
        this.objectKey !== "movies" ||
        !this.showRightsAvailabilityFilters
      ) {
        return "";
      }

      return this.getMoviesObjectFilterParts().join(", ");
    },
    getMoviesObjectFilterParts() {
      if (
        this.objectKey !== "movies" ||
        !this.showRightsAvailabilityFilters
      ) {
        return [];
      }

      return this.objectFiltersDef
        .map((item) => {
          const display = this.getMoviesObjectFilterDisplayValue(item);
          return display ? `${item.name}: ${display}` : null;
        })
        .filter(Boolean);
    },
    getReportColumnHeader(col) {
      if (!col?.name) {
        return "";
      }

      const label = col.name.charAt(0).toUpperCase() + col.name.slice(1);
      return label.replace(/_/g, " ");
    },
    onShowRightsAvailabilityFiltersChange() {
      this.saveMoviesObjectFiltersToStorage();
    },
    saveMoviesObjectFiltersToStorage() {
      if (this.objectKey !== "movies") {
        return;
      }

      localStorage.setItem(
        this.moviesObjectFiltersStorageKey,
        JSON.stringify({
          values: this.moviesObjectFilters.values,
          text: this.moviesObjectFilters.text,
          show: this.showRightsAvailabilityFilters,
        }),
      );
    },
    restoreMoviesObjectFiltersFromStorage() {
      const stored = localStorage.getItem(this.moviesObjectFiltersStorageKey);
      if (!stored) {
        return;
      }

      try {
        const parsed = JSON.parse(stored);
        this.moviesObjectFilters.values = parsed.values || {};
        this.moviesObjectFilters.text = parsed.text || {};
        this.showRightsAvailabilityFilters = parsed.show === true;
        this.rebuildMoviesObjectFiltersUi();
      } catch (error) {
        console.error("Error restoring movies object filters", error);
      }
    },
    async loadMoviesObjectFilterOptions() {
      for (const item of this.objectFiltersDef) {
        if (!["select", "multi-select", "tree-select"].includes(item.type)) {
          continue;
        }

        if (this.objectFiltersOptions[item.list_route]?.length) {
          continue;
        }

        this.$set(this.objectFiltersOptions, item.list_route, []);
        const res = await getMasterList({ list_route: item.list_route });
        this.objectFiltersOptions[item.list_route].splice(
          0,
          this.objectFiltersOptions[item.list_route].length,
          ...res.data.data,
        );
      }
    },
    rebuildMoviesObjectFiltersUi() {
      this.moviesObjectFilters.ui = {};

      for (const item of this.objectFiltersDef) {
        const stored = this.moviesObjectFilters.values[item.key];
        if (stored == null) {
          continue;
        }

        if (item.type === "multi-select") {
          this.$set(
            this.moviesObjectFilters.ui,
            item.key,
            (this.objectFiltersOptions[item.list_route] || []).filter((opt) =>
              stored.includes(opt.id),
            ),
          );
        } else if (item.type === "boolean") {
          this.$set(
            this.moviesObjectFilters.ui,
            item.key,
            stored === true
              ? this.booleanOptions.find((opt) => opt.value === true)
              : this.booleanOptions.find((opt) => opt.value === false),
          );
        } else if (item.type === "select") {
          this.$set(
            this.moviesObjectFilters.ui,
            item.key,
            (this.objectFiltersOptions[item.list_route] || []).find(
              (opt) => opt.id === stored,
            ),
          );
        }
      }
    },
    onMoviesObjectFilterInput(value, item) {
      if (item.type === "multi-select") {
        this.$set(
          this.moviesObjectFilters.values,
          item.key,
          value.map((opt) => opt.id),
        );
        this.$set(
          this.moviesObjectFilters.text,
          item.key,
          value.map((opt) => opt.name).join(", "),
        );
      } else if (item.type === "select") {
        this.$set(this.moviesObjectFilters.values, item.key, value ? value.id : null);
        this.$set(this.moviesObjectFilters.text, item.key, value ? value.name : "");
      } else if (item.type === "boolean") {
        if (!value) {
          this.$delete(this.moviesObjectFilters.values, item.key);
          this.$delete(this.moviesObjectFilters.text, item.key);
        } else {
          this.$set(this.moviesObjectFilters.values, item.key, value.value);
          this.$set(this.moviesObjectFilters.text, item.key, value.name);
        }
      } else if (item.type === "tree-select") {
        this.$set(this.moviesObjectFilters.values, item.key, value);
        this.$set(
          this.moviesObjectFilters.text,
          item.key,
          getTreeLabelsById(value, this.objectFiltersOptions[item.list_route]),
        );
      } else if (item.type === "date") {
        const dateStr = Array.isArray(value)
          ? typeof value[1] === "string"
            ? value[1]
            : value[0]
          : value;

        if (!dateStr) {
          this.$delete(this.moviesObjectFilters.values, item.key);
          this.$delete(this.moviesObjectFilters.text, item.key);
        } else {
          this.$set(this.moviesObjectFilters.values, item.key, dateStr);
          this.$set(this.moviesObjectFilters.text, item.key, dateStr);
        }
      } else {
        this.$set(this.moviesObjectFilters.values, item.key, value);
        this.$set(this.moviesObjectFilters.text, item.key, value);
      }

      this.saveMoviesObjectFiltersToStorage();
    },
    resetMoviesObjectFilters(persist = true) {
      this.moviesObjectFilters = {
        values: {},
        text: {},
        ui: {},
      };
      this.showRightsAvailabilityFilters = false;

      if (persist) {
        localStorage.removeItem(this.moviesObjectFiltersStorageKey);
      }
    },
    // Resizer methods
    startResize(event) {
      event.preventDefault();
      event.stopPropagation();

      this.isResizing = true;
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      this.resizeStartX = clientX;
      this.resizeStartWidth = this.leftPanelWidth;

      // Use arrow functions to maintain 'this' context
      this.handleMouseMove = (e) => this.handleResize(e);
      this.handleMouseUp = () => this.stopResize();
      this.handleTouchMove = (e) => {
        e.preventDefault();
        this.handleResize(e);
      };
      this.handleTouchEnd = () => this.stopResize();

      document.addEventListener("mousemove", this.handleMouseMove);
      document.addEventListener("mouseup", this.handleMouseUp);
      document.addEventListener("touchmove", this.handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", this.handleTouchEnd);

      // Prevent text selection while resizing
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    },
    handleResize(event) {
      if (!this.isResizing) return;

      const container = this.$refs.resizeContainer;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const deltaX = clientX - this.resizeStartX;
      const containerWidth = rect.width;

      if (containerWidth === 0) return;

      const deltaPercent = (deltaX / containerWidth) * 100;
      let newWidth = this.resizeStartWidth + deltaPercent;

      // Constrain between 25% and 75%
      if (newWidth < 25) newWidth = 25;
      if (newWidth > 75) newWidth = 75;

      this.leftPanelWidth = Math.round(newWidth * 10) / 10; // Keep one decimal for smoother resizing
    },
    stopResize() {
      if (!this.isResizing) return;

      this.isResizing = false;

      // Remove event listeners
      if (this.handleMouseMove) {
        document.removeEventListener("mousemove", this.handleMouseMove);
      }
      if (this.handleMouseUp) {
        document.removeEventListener("mouseup", this.handleMouseUp);
      }
      if (this.handleTouchMove) {
        document.removeEventListener("touchmove", this.handleTouchMove);
      }
      if (this.handleTouchEnd) {
        document.removeEventListener("touchend", this.handleTouchEnd);
      }

      // Restore text selection and cursor
      document.body.style.userSelect = "";
      document.body.style.cursor = "";

      // Clean up handlers
      this.handleMouseMove = null;
      this.handleMouseUp = null;
      this.handleTouchMove = null;
      this.handleTouchEnd = null;
    },
  },
  mounted() {},
  beforeMount() {
    this.getObjects();
  },
  beforeDestroy() {
    window.removeEventListener("abort-report", this.abortListener);
    this.stopResize();
  },
};
</script>

<style lang="scss" scoped>
// Modern Modal Styles
::v-deep .modern-modal .modal-content {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.15);
}

::v-deep .modern-modal .modal-body {
  padding: 0.5rem 0.75rem;
}

.modal-card {
  border: none;
  padding: 0;
}

.floating-report {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 260px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
  padding: 0.75rem;
  z-index: 9999;
}

.floating-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  margin-bottom: 0.4rem;
}

.floating-header button {
  padding: 0;
  font-size: 0.8rem;
}

.modal-header-section {
  margin-bottom: 0.5rem;
}

.modal-header-section small {
  font-size: 0.8rem;
  line-height: 1.3;
}

.modal-header-section h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.custom-progress {
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
}

.custom-progress ::v-deep .progress-bar {
  background: linear-gradient(90deg, #6b7280, #4b5563);
}

// Header Card
.header-card {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
  padding: 1rem;
}

.form-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 0.15rem;
}

.form-label-sm {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.modern-input,
.modern-select ::v-deep .vs__dropdown-toggle {
  border-radius: 8px;
  font-size: 0.875rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  transition: all 0.2s ease;
}

.modern-input:focus,
.modern-select ::v-deep .vs__dropdown-toggle:focus {
  border-color: #9ca3af;
  box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.1);
}

.search-input {
  border-radius: 8px;
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

// Resizable Container
.resizable-container-wrapper {
  width: 100%;
}

.resizable-container {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 500px;
  gap: 0;
  position: relative;
}

.resizable-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.resizable-panel-left,
.resizable-panel-right {
  transition: width 0.1s ease;
}

// Disable transition when resizing for smoother experience
.resizable-container.resizing .resizable-panel-left,
.resizable-container.resizing .resizable-panel-right {
  transition: none;
}

// Resizer Handle
.resizer-handle {
  width: 12px;
  min-width: 12px;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-left: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  position: relative;
  user-select: none;
  flex-shrink: 0;
  z-index: 10;
  touch-action: none;
}

.resizer-handle:hover {
  background: #e5e7eb;
  border-left-color: #9ca3af;
  border-right-color: #9ca3af;
}

.resizer-handle:active,
.resizer-handle.resizing {
  background: #d1d5db;
  border-left-color: #6b7280;
  border-right-color: #6b7280;
}

.resizer-handle.resizing {
  cursor: col-resize !important;
}

.resizer-icon {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}

.resizer-dots {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #9ca3af;
}

// Labels Card
.labels-card,
.output-card {
  background: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);

  .card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0.75rem;
    padding-bottom: 0.75rem;
  }
}

.card-header-section {
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 0.5rem;
}

.card-header-section h6 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.count-badge {
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  background-color: #6b7280;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  font-size: 0.8rem;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
}

// Labels Container
.labels-container {
  flex: 1;
  overflow-y: auto;
  padding-top: 0.5rem;
  min-height: 200px;
}

.labels-draggable {
  min-height: 150px;
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-content: flex-start;
}

.label-item {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  color: #374151;
  cursor: grab;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: all 0.15s ease;
}

.label-item:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.1);
}

.label-item:active {
  cursor: grabbing;
}

.label-text {
  max-width: 150px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.filter-badge {
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  font-weight: 700;
  background-color: #fbbf24 !important;
  color: #78350f !important;
  border: 1px solid #f59e0b;
  min-width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

// Output Labels Container
.output-labels-container {
  flex: 1;
  overflow-y: auto;
  padding-top: 0.5rem;
  min-height: 280px;
}

.output-draggable {
  min-height: 250px;
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-content: flex-start;
}

.output-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.35rem 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  border-left: 2px solid transparent;
  flex: 0 0 calc(50% - 0.175rem);
  max-width: calc(50% - 0.175rem);
  min-width: 0;
}

.output-item:hover {
  border-color: #9ca3af;
  border-left-color: #6b7280;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
  transform: translateX(2px);
}

.output-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.2rem;
  gap: 0.3rem;
}

.output-item-title-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 0.25rem;
}

.column-number-badge {
  font-size: 0.6rem;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  font-weight: 600;
  flex-shrink: 0;
}

.output-item-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.filter-indicator {
  font-size: 0.6rem;
  padding: 0.08rem 0.3rem;
  border-radius: 3px;
  flex-shrink: 0;
}

.output-item-actions {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
}

.filter-config-btn,
.remove-btn {
  padding: 0.2rem 0.45rem;
  font-size: 0.7rem;
  border-radius: 4px;
  min-width: auto;
  height: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-config-btn {
  border-color: #9ca3af;
  color: #6b7280;
}

.filter-config-btn:hover {
  border-color: #6b7280;
  color: #374151;
  background-color: #f3f4f6;
}

.filter-config-btn ::v-deep svg,
.remove-btn ::v-deep svg {
  width: 12px;
  height: 12px;
  stroke-width: 2;
}

.filter-config-btn ::v-deep svg {
  color: #6b7280;
  stroke: #6b7280;
}

.filter-config-btn:hover ::v-deep svg {
  color: #374151;
  stroke: #374151;
}

.remove-btn ::v-deep svg {
  color: #dc2626;
  stroke: #dc2626;
}

.output-item-content {
  margin-top: 0.3rem;
  padding-top: 0.3rem;
  border-top: 1px solid #f3f4f6;
}

.section-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
  margin-bottom: 0.25rem;
}

.filters-section,
.pivot-section {
  margin-bottom: 0.3rem;
}

.filter-tags,
.pivot-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  width: 100%;
}

.filter-tag {
  font-size: 0.65rem;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  color: #374151;
}

.filter-tag ::v-deep {
  color: #374151 !important;
  background-color: #f3f4f6 !important;
}

.filter-tag-text {
  max-width: 200px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: #374151 !important;
  font-weight: 500;
  display: inline-block;
  line-height: 1.3;
}

.filter-remove {
  cursor: pointer;
  color: #dc2626;
}

.filter-remove:hover {
  color: #991b1b;
}

.pivot-badge {
  font-size: 0.65rem;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

// Section Card (in modals)
.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.4rem;
  background: #fafafa;
}

.section-header {
  display: flex;
  align-items: center;
  color: #374151;
  font-size: 0.8rem;
  margin-bottom: 0.4rem;
}

// Reduce form group margins in modal
.modal-card ::v-deep .form-group {
  margin-bottom: 0.5rem;
}

.modal-card ::v-deep .form-group:last-child {
  margin-bottom: 0;
}

// Empty State
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: #9ca3af;
}

.empty-state-card {
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #fafafa;
}

.empty-state-content {
  padding: 2rem;
}

// Preview Table
.table-container {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 400px;
}

.preview-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  width: 100%;
}

.preview-table th {
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  padding: 0.5rem;
  position: sticky;
  top: 0;
  z-index: 10;
}

.preview-table td {
  border-bottom: 1px solid #f3f4f6;
  padding: 0.5rem;
}

.preview-table tr:hover {
  background: #f9fafb;
}

// Drag and Drop
.ghost {
  opacity: 0.5;
  background: #e0e7ff;
  border: 2px dashed #6366f1;
}

.flip-list-move {
  transition: transform 0.3s ease;
}

// Responsive
@media (max-width: 992px) {
  .resizable-container {
    flex-direction: column;
    min-height: auto;
  }

  .resizable-panel-left,
  .resizable-panel-right {
    width: 100% !important;
  }

  .resizer-handle {
    display: none;
  }

  .labels-card,
  .output-card {
    margin-bottom: 1rem;
  }
}

@media (max-width: 768px) {
  .header-card {
    padding: 0.75rem;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .action-btn {
    width: 100%;
  }

  .output-item {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .output-item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .output-item-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

// Scrollbar styling
.labels-container::-webkit-scrollbar,
.output-labels-container::-webkit-scrollbar {
  width: 6px;
}

.labels-container::-webkit-scrollbar-track,
.output-labels-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.labels-container::-webkit-scrollbar-thumb,
.output-labels-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.labels-container::-webkit-scrollbar-thumb:hover,
.output-labels-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
